import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import ReviewFormModal from './components/ReviewFormModal';

import WeatherWidget from '../spot-guide/components/WeatherWidget';

import { spotService } from '../../services/spotService';
import { reviewService } from '../../services/reviewService';
import { weatherService } from '../../services/weatherService';

const SpotDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [weather, setWeather] = useState(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const [currentMainImage, setCurrentMainImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryView, setGalleryView] = useState('masonry'); // 'masonry' or 'grid'
  const [selectedCategory, setSelectedCategory] = useState('all');

  // PREPARE GALLERY DATA - Move to top before return
  const mainImage = spot?.image_url || spot?.image || spot?.imageUrl;
  const galleryImages = spot?.gallery_urls || spot?.gallery || [];
  const displayPhotos = [mainImage, ...galleryImages]?.filter(Boolean)?.slice(0, 3);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;

      if (e?.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e?.key === 'ArrowLeft' && lightboxIndex > 0) {
        const newIndex = lightboxIndex - 1;
        setLightboxIndex(newIndex);
        setCurrentMainImage([spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.[newIndex]);
      } else if (e?.key === 'ArrowRight' && lightboxIndex < [spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.length - 1) {
        const newIndex = lightboxIndex + 1;
        setLightboxIndex(newIndex);
        setCurrentMainImage([spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.[newIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, lightboxIndex, spot]);

  const mockReviews = [
  {
    id: 1,
    userName: "Elif Şahin",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17caa9ce8-1763301418543.png",
    userAvatarAlt: "Profesyonel kadın portresi sarı saçlı gülümseyen Türk kadın pembe tişört ile",
    rating: 5,
    comment: "Harika bir spot! Su çok sakin ve manzara muhteşem. Başlangıç seviyesi için ideal. Otopark sorunu yok ve tesis temiz.",
    date: new Date('2025-12-15'),
    visitDate: new Date('2025-12-10'),
    isVerified: true,
    helpful: 12,
    photos: []
  },
  {
    id: 2,
    userName: "Burak Yıldız",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_179aaf74c-1763296407432.png",
    userAvatarAlt: "Profesyonel erkek portresi kısa siyah saçlı sakallı gülümseyen Türk erkek gri tişört ile",
    rating: 4,
    comment: "Güzel bir yer ama hafta sonları biraz kalabalık oluyor. Sabah erken saatlerde gitmek daha iyi. Kiralama imkanları var.",
    date: new Date('2025-12-12'),
    visitDate: new Date('2025-12-08'),
    isVerified: true,
    helpful: 8,
    photos: []
  }];


  // Fetch real spot data from Supabase
  useEffect(() => {
    const fetchSpotDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await spotService?.getSpotById(id);

        if (!data) {
          throw new Error('Spot bulunamadı');
        }

        setSpot(data);
        setCurrentMainImage(data?.image);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching spot details:', err);
        setError(err?.message || 'Spot detayları yüklenemedi');
        setIsLoading(false);
      }
    };

    if (id && id !== ':id') {
      fetchSpotDetails();
    } else if (!id || id === ':id') {
      setIsLoading(false);
      setError('Geçersiz spot bağlantısı');
    }
  }, [id]);

  // Fetch real-time weather data when spot coordinates are available
  useEffect(() => {
    const fetchWeatherData = async () => {
      if (!spot?.coordinates?.lat || !spot?.coordinates?.lng) {
        return;
      }

      try {
        setIsLoadingWeather(true);
        const weatherData = await weatherService?.getSpotWeather(
          spot?.coordinates?.lat,
          spot?.coordinates?.lng
        );
        setWeather(weatherData);
      } catch (err) {
        console.error('Error fetching weather data:', err);
        setWeather(null);
      } finally {
        setIsLoadingWeather(false);
      }
    };

    fetchWeatherData();
  }, [spot?.coordinates]);

  // Fetch real reviews from Supabase
  useEffect(() => {
    const fetchReviews = async () => {
      if (!id) return;

      try {
        setIsLoadingReviews(true);
        const reviewsData = await reviewService?.getApprovedReviewsForSpot(id);
        setReviews(reviewsData);
      } catch (err) {
        console.error('Error fetching reviews:', err);
      } finally {
        setIsLoadingReviews(false);
      }
    };

    fetchReviews();
  }, [id]);

  const handleReviewSubmitted = async () => {
    // Refresh reviews list after new review submitted
    try {
      const reviewsData = await reviewService?.getApprovedReviewsForSpot(id);
      setReviews(reviewsData);
    } catch (err) {
      console.error('Error refreshing reviews:', err);
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Başlangıç': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Orta': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'İleri': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors?.[difficulty] || colors?.['Orta'];
  };

  // Loading State
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Spot Detayları - PaddleTurkiye</title>
        </Helmet>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <LoadingSpinner message="Spot detayları yükleniyor..." />
          </div>
        </div>
      </>);

  }

  // Error State - Friendly 404
  if (error || !spot) {
    return (
      <>
        <Helmet>
          <title>Spot Bulunamadı - PaddleTurkiye</title>
        </Helmet>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-md">
                <div className="bg-orange-100 p-4 rounded-full inline-flex mb-4">
                  <Icon name="MapPin" size={48} className="text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Spot Bulunamadı</h2>
                <p className="text-muted-foreground mb-6">
                  {error || 'Aradığınız spot bulunamadı veya silinmiş olabilir.'}
                </p>
                <Button
                  variant="default"
                  iconName="ArrowLeft"
                  onClick={() => navigate('/spot-guide')}>

                  Spot Rehberine Geri Dön
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>);

  }

  return (
    <>
      <Helmet>
        <title>{spot?.name} - Spot Detayları - PaddleTurkiye</title>
        <meta name="description" content={spot?.description} />
      </Helmet>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            onClick={() => navigate('/spot-guide')}
            className="mb-3">

            Spot Rehberine Dön
          </Button>

          {/* UNIFIED GRID - Single Container Rule */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            
            {/* LEFT COLUMN (Span 2) - Contains EVERYTHING on the left */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* 1. Photo Gallery (Clean Grid Implementation) */}
              <div className="w-full h-[300px] md:h-[450px] lg:h-[500px] grid grid-cols-1 md:grid-cols-3 gap-2 rounded-3xl overflow-hidden shadow-lg">
                {displayPhotos?.length > 0 ?
                <>
                    {/* Main photo - spans 2 columns */}
                    <button
                    onClick={() => {setLightboxIndex(0);setIsLightboxOpen(true);}}
                    className="relative md:col-span-2 group h-full w-full">
                    
                      <Image
                      src={displayPhotos?.[0]}
                      alt="Main spot photo"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </button>

                    {/* Side photos stacked - 1 column */}
                    <div className="hidden md:flex flex-col gap-2">
                      {displayPhotos?.[1] &&
                    <button
                      onClick={() => {setLightboxIndex(1);setIsLightboxOpen(true);}}
                      className="relative group h-full w-full">
                      
                          <Image
                        src={displayPhotos?.[1]}
                        alt="Detail photo 1"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                        </button>
                    }
                      
                      {displayPhotos?.[2] &&
                    <button
                      onClick={() => {setLightboxIndex(2);setIsLightboxOpen(true);}}
                      className="relative group h-full w-full">
                      
                          <Image
                        src={displayPhotos?.[2]}
                        alt="Detail photo 2"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                        </button>
                    }
                    </div>
                  </> :

                <div className="col-span-3 flex items-center justify-center bg-muted">
                    <Icon name="Image" className="text-muted-foreground w-12 h-12" />
                  </div>
                }
              </div>

              {/* 2. Description Section */}
              <div className="bg-card rounded-xl p-6 shadow-organic">
                <h2 className="text-xl font-bold mb-3">Spot Hakkında</h2>
                <p className="text-foreground leading-relaxed">{spot?.description}</p>
              </div>

              {/* 3. Facilities */}
              {spot?.facilities && spot?.facilities?.length > 0 &&
              <div className="bg-card rounded-xl p-6 shadow-organic">
                  <h2 className="text-xl font-bold text-foreground mb-3">İmkanlar</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {spot?.facilities?.map((facility, index) =>
                  <div key={index} className="flex items-center gap-2 text-foreground">
                        <Icon name="Check" size={18} className="text-green-600" />
                        <span>{facility}</span>
                      </div>
                  )}
                  </div>
                </div>
              }

              {/* 4. Hazards */}
              {spot?.hazards && spot?.hazards?.length > 0 &&
              <div className="bg-warning/10 rounded-xl p-6 border border-warning/20">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0" />
                    <div>
                      <h2 className="text-lg font-bold text-foreground mb-2">Dikkat Edilmesi Gerekenler</h2>
                      <ul className="space-y-1.5">
                        {spot?.hazards?.map((hazard, index) =>
                      <li key={index} className="text-foreground flex items-start gap-2">
                            <span className="text-warning mt-1">•</span>
                            <span>{hazard}</span>
                          </li>
                      )}
                      </ul>
                    </div>
                  </div>
                </div>
              }

              {/* 5. Reviews Section */}
              <div className="bg-card rounded-xl p-6 shadow-organic">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground">
                    Yorumlar ({reviews?.length})
                  </h2>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="MessageSquarePlus"
                    onClick={() => setIsReviewModalOpen(true)}>
                    
                    Yorum Yaz
                  </Button>
                </div>

                {isLoadingReviews ?
                <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-muted-foreground text-sm">Yorumlar yükleniyor...</p>
                  </div> :
                reviews?.length > 0 ?
                <div className="space-y-3">
                    {reviews?.map((review) =>
                  <div key={review?.id} className="border-b border-border pb-3 last:border-0 last:pb-0">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                            {review?.user?.avatarUrl ?
                        <img
                          src={review?.user?.avatarUrl}
                          alt={review?.user?.fullName}
                          className="w-full h-full object-cover" /> :


                        <Icon name="User" size={20} className="text-muted-foreground" />
                        }
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground">
                                {review?.user?.fullName || 'Anonim Kullanıcı'}
                              </span>
                              <div className="flex items-center gap-1">
                                {[...Array(5)]?.map((_, i) =>
                            <Icon
                              key={i}
                              name="Star"
                              size={14}
                              className={i < review?.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'} />

                            )}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {new Date(review.createdAt)?.toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                            </p>
                          </div>
                        </div>
                        <p className="text-foreground">{review?.comment}</p>
                        
                        {review?.imageUrls && review?.imageUrls?.length > 0 &&
                    <div className="grid grid-cols-3 gap-2 mt-2">
                            {review?.imageUrls?.map((url, index) =>
                      <img
                        key={index}
                        src={url}
                        alt={`Review photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg" />

                      )}
                          </div>
                    }
                      </div>
                  )}
                  </div> :

                <div className="text-center py-6">
                    <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-foreground font-medium mb-1">
                      Henüz yorum yapılmamış
                    </p>
                    <p className="text-sm text-muted-foreground">
                      İlk yorumu sen yap!
                    </p>
                  </div>
                }
              </div>
            </div>

            {/* RIGHT COLUMN (Span 1) - Sidebar */}
            <div className="space-y-6 lg:sticky lg:top-24">
              {/* Info Card */}
              <div className="bg-card rounded-xl p-4 shadow-organic">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  {spot?.name}
                </h1>
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Icon name="MapPin" size={18} />
                  <span>{spot?.location}</span>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Star" size={20} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-lg font-semibold text-foreground">{spot?.rating}</span>
                  <span className="text-muted-foreground">({spot?.reviewCount} değerlendirme)</span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-foreground">
                      <Icon name="Car" size={18} className="text-muted-foreground" />
                      <span>Otopark</span>
                    </div>
                    <span className="font-medium">{spot?.parking}</span>
                  </div>

                  {spot?.waterCondition &&
                  <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-foreground">
                        <Icon name="Waves" size={18} className="text-muted-foreground" />
                        <span>Su Durumu</span>
                      </div>
                      <span className="font-medium">{spot?.waterCondition}</span>
                    </div>
                  }

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-foreground">
                      <Icon name="Banknote" size={18} className="text-muted-foreground" />
                      <span>Giriş Ücreti</span>
                    </div>
                    <span className="font-medium">{spot?.entryFee}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-foreground">
                      <Icon name="Users" size={18} className="text-muted-foreground" />
                      <span>Son Ziyaretçi</span>
                    </div>
                    <span className="font-medium">{spot?.recentVisitors} kişi</span>
                  </div>

                  {spot?.bestTime &&
                  <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-foreground">
                        <Icon name="Calendar" size={18} className="text-muted-foreground" />
                        <span>En İyi Zaman</span>
                      </div>
                      <span className="font-medium">{spot?.bestTime}</span>
                    </div>
                  }

                  {spot?.accessibility &&
                  <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-foreground">
                        <Icon name="Navigation" size={18} className="text-muted-foreground" />
                        <span>Erişim</span>
                      </div>
                      <span className="font-medium">{spot?.accessibility}</span>
                    </div>
                  }
                </div>

                <Button
                  fullWidth
                  iconName="Navigation"
                  className="mt-4"
                  onClick={() => window.open(`https://maps.google.com/?q=${spot?.coordinates?.lat},${spot?.coordinates?.lng}`, '_blank')}>
                  
                  Yol Tarifi Al
                </Button>
              </div>

              {/* Weather Widget */}
              <WeatherWidget
                weather={weather}
                isLoading={isLoadingWeather}
                spotName={spot?.name}
                coordinates={spot?.coordinates} />
              
            </div>
          </div>
        </div>
      </div>
      {/* ENHANCED LIGHTBOX MODAL - Premium Design */}
      {isLightboxOpen &&
      <div
        className="fixed inset-0 bg-black z-50 flex items-center justify-center animate-in fade-in duration-300"
        onClick={(e) => {
          if (e?.target === e?.currentTarget) setIsLightboxOpen(false);
        }}>
        
          {/* Modern Header Bar */}
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 md:p-6 z-30">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-3">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-2">
                  <Icon name="Image" size={20} className="text-white" />
                </div>
                <div className="text-white">
                  <h3 className="font-semibold text-lg">{spot?.name}</h3>
                  <p className="text-sm text-white/70">Fotoğraf {lightboxIndex + 1} / {[spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.length}</p>
                </div>
              </div>
              <button
              onClick={() => setIsLightboxOpen(false)}
              className="text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all duration-300 backdrop-blur-md group"
              aria-label="Kapat">
              
                <Icon name="X" size={24} className="group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
          </div>

          {/* Navigation Arrows - Modern Design */}
          {lightboxIndex > 0 &&
        <button
          onClick={() => {
            const newIndex = lightboxIndex - 1;
            setLightboxIndex(newIndex);
          }}
          className="absolute left-4 md:left-8 text-white bg-white/10 hover:bg-white/20 rounded-full p-4 transition-all duration-300 z-30 backdrop-blur-md transform hover:scale-110 hover:-translate-x-2"
          aria-label="Önceki fotoğraf">
          
              <Icon name="ChevronLeft" size={32} />
            </button>
        }

          {lightboxIndex < [spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.length - 1 &&
        <button
          onClick={() => {
            const newIndex = lightboxIndex + 1;
            setLightboxIndex(newIndex);
          }}
          className="absolute right-4 md:right-8 text-white bg-white/10 hover:bg-white/20 rounded-full p-4 transition-all duration-300 z-30 backdrop-blur-md transform hover:scale-110 hover:translate-x-2"
          aria-label="Sonraki fotoğraf">
          
              <Icon name="ChevronRight" size={32} />
            </button>
        }

          {/* Main Image with Smooth Transition */}
          <div className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4 md:p-20">
            <div className="relative max-w-full max-h-[80vh] w-full h-full animate-in zoom-in-95 duration-500">
              <Image
              src={[spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.[lightboxIndex]}
              alt={`${spot?.name} tam ekran ${lightboxIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" />
            
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6 z-30">
            <div className="max-w-7xl mx-auto">
              {/* Thumbnail Navigation */}
              {[spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.length > 1 &&
            <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {[spot?.image, ...(spot?.gallery || [])]?.filter(Boolean)?.map((photoUrl, index) =>
              <button
                key={index}
                onClick={() => setLightboxIndex(index)}
                className={`relative flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all duration-300 border-2 ${
                lightboxIndex === index ?
                'border-white scale-110 shadow-xl' :
                'border-white/30 opacity-50 hover:opacity-100 hover:scale-105'}`
                }>
                
                      <Image
                  src={photoUrl}
                  alt={`Küçük resim ${index + 1}`}
                  className="w-full h-full object-cover" />
                
                      {lightboxIndex === index &&
                <div className="absolute inset-0 bg-white/20 flex items-center justify-center">
                          <Icon name="Check" size={20} className="text-white" />
                        </div>
                }
                    </button>
              )}
                </div>
            }

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 backdrop-blur-md">
                    <Icon name="Download" size={18} />
                    <span className="text-sm font-medium hidden sm:inline">İndir</span>
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 backdrop-blur-md">
                    <Icon name="Share2" size={18} />
                    <span className="text-sm font-medium hidden sm:inline">Paylaş</span>
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 backdrop-blur-md">
                    <Icon name="Heart" size={18} />
                    <span className="text-sm font-medium hidden sm:inline">Beğen</span>
                  </button>
                </div>
                <div className="text-white/70 text-sm hidden md:flex items-center gap-2">
                  <Icon name="Keyboard" size={16} />
                  <span>← → ok tuşları ile gezin</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      {/* Review Form Modal */}
      <ReviewFormModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        spotId={id}
        spotName={spot?.name}
        onReviewSubmitted={handleReviewSubmitted} />
    </>);



};

export default SpotDetails;